/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import InfinityModel from 'ember-infinity/lib/infinity-model';
import { defaultMatcher, filterOptions } from 'ember-power-select/utils/group-utils';
import BvSelectComponent from 'volta/components/bv-select';
import BvSelectMultiComponent from 'volta/components/bv-select-multi';
import { isThenable } from 'volta/utils/object-utils';

import { action, get } from '@ember/object';
import { inject as service } from '@ember/service';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';

import type { MatcherFn } from 'ember-power-select/utils/group-utils';
import type { IntlService } from 'ember-intl';
import type { Select } from 'ember-power-select/components/power-select';
import type { BvSelectArgs } from 'volta/components/bv-select';
import type { BvSelectMultiArgs } from 'volta/components/bv-select-multi';
import { task } from 'ember-concurrency';
import { defaultErrorHandler, type TError } from 'volta/utils/error-utils';

export type SuggestionOption = {
  __isSuggestion__: boolean;
  __value__: string;
  text: string;
};
export type DataOption = string | Record<string, unknown>;
export type SelectedOption<O = DataOption> = SuggestionOption | O;

export interface BvSelectCreateSignature<O = DataOption> {
  Args: (BvSelectArgs | BvSelectMultiArgs) & {
    value?: string | null;
    multiple?: boolean;
    matcher?: MatcherFn;
    infiniteScroll?: boolean;
    onChange: (selected: SelectedOption<O> | SelectedOption<O>[], select: Select, e: Event) => void;
    onCreate?: (term: string | null | undefined, select: Select, event: Event) => void;
    onKeydown?: (select: Select, event: KeyboardEvent) => void;
    search?: (term: string, select: Select) => O[] | InfinityModel;
    buildSuggestion?: (term: string) => string;
    showCreateWhen?: (term: string, options: O[]) => boolean;
    options?: O[] | Promise<O[]>;
    showCreatePosition?: 'top' | 'bottom';
    renderAll?: boolean;
    triggerAutoFocus?: boolean;
    inputClasses?: string;
  };
  Element: HTMLDivElement;
  Blocks: {
    default: [any, Select];
  };
}

export default class BvSelectCreate<O = DataOption> extends Component<BvSelectCreateSignature<O>> {
  @service intl!: IntlService;

  select?: Select;

  matcher: MatcherFn = this.args.matcher ?? defaultMatcher;

  get selectComponent() {
    return ensureSafeComponent(
      this.args.multiple ? BvSelectMultiComponent : BvSelectComponent,
      this
    );
  }

  // Getters & CPs
  // ~~~~~~

  get optionsArray() {
    const { options, infiniteScroll } = this.args;
    // Because we use ember-infinity for infinite scrolling
    // we don't want to change the type of the response
    if (infiniteScroll) {
      return options;
    }
    if (!options) {
      return [];
    }
    if (isThenable(options as Promise<SelectedOption[]>)) {
      return (options as Promise<SelectedOption[]>).then((value) => value.slice());
    } else {
      return (options as SelectedOption[]).slice();
    }
  }

  // Lifecycle hooks
  // ~~~~~~

  // Actions
  // ~~~~~~

  @action
  handleRegisterAPI(select: Select) {
    this.select = select;
    this.args.registerAPI?.(select);
  }

  @action
  onKeydown(select: Select, event: KeyboardEvent) {
    if (event.key === 'Enter' && select.results.length === 1) {
      this.selectOrCreate(this.args.multiple ? select.results : select.results[0], select, event);
      select.actions.close();

      return false;
    }

    return this.args.onKeydown?.(select, event);
  }

  @action
  onClear(e: MouseEvent) {
    e.stopPropagation();
    this.select?.actions.select(null);
    if (e.type === 'touchstart') {
      return false;
    }
    return;
  }

  searchAndSuggest = task(async (term: string | undefined, select: Select) => {
    try {
      if (!term || term.length === 0) {
        return this.args.options;
      }
      const searchResults = (await this.args.search?.(term, select)) ?? this.args.options;
      const isInfinityModel = searchResults instanceof InfinityModel;
      const options = this.addCreateOption(
        term,
        isInfinityModel
          ? ((searchResults as unknown as InfinityModel).content as O[]).slice()
          : (searchResults as O[])
      );

      if (isInfinityModel) {
        (searchResults as unknown as InfinityModel).setObjects(options);
        return searchResults;
      }
      return options;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return this.args.options;
    }
  });

  @action
  selectOrCreate(
    selection: SelectedOption<O> | SelectedOption<O>[],
    select: Select,
    e: MouseEvent | KeyboardEvent
  ) {
    const suggestion = this.args.multiple
      ? (selection as (unknown | SuggestionOption)[]).filter(
          (option) => (option as SuggestionOption).__isSuggestion__
        )[0]
      : selection && (selection as SuggestionOption).__isSuggestion__
        ? selection
        : undefined;

    if (suggestion) {
      this.args.onCreate?.((suggestion as SuggestionOption).__value__, select, e);
    } else {
      this.args.onChange(selection, select, e);
    }
  }

  // Methods
  // ~~~~~~

  shouldShowCreateOption(term: string, options: O[] = []) {
    return this.args.onCreate && (this.args.showCreateWhen?.(term, options) ?? true);
  }

  addCreateOption(term: string, results: O[]): SelectedOption<O>[] {
    if (this.shouldShowCreateOption(term, results)) {
      if (this.args.showCreatePosition === 'bottom') {
        return [...(results ?? []), this.buildSuggestionForTerm(term)];
      } else {
        return [this.buildSuggestionForTerm(term), ...(results ?? [])];
      }
    }
    return [...results];
  }

  filter(options: unknown[], searchText: string) {
    let matcher: MatcherFn;
    if (this.args.searchField) {
      matcher = (option: unknown, text: string) =>
        this.matcher(get(option, this.args.searchField!), text);
    } else {
      matcher = (option: unknown, text: string) => this.matcher(option, text);
    }
    return filterOptions(options || [], searchText, matcher);
  }

  buildSuggestionForTerm(term: string): SuggestionOption {
    return {
      __isSuggestion__: true,
      __value__: term,
      text: this.buildSuggestionLabel(term)
    };
  }

  buildSuggestionLabel(term: string) {
    return this.args.buildSuggestion?.(term) ?? `${this.intl.t('add')} "${term}"...`;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvSelectCreate: typeof BvSelectCreate;
    'bv-select-create': typeof BvSelectCreate;
  }
}
